// This comment tells ESLint that __webpack_public_path__ is a global variable so it doesn't complain about it
/* global __webpack_public_path__ */
import type { ListResponseMetadata } from "@eai-toolkit/client";
import { AllowedApi } from "@eai-toolkit/client";
import assert from "assert";
import type { AxiosError, AxiosRequestHeaders, AxiosResponse } from "axios";
import axios from "axios";

import { hasKey } from "@helpers/utils";

import { HttpException } from "./exceptions";
import {
  httpCodeToErrorStatus,
  httpCodeToPermissionStatus,
  type ResourceGetErrorStatus,
  type ResourcePermissionStatus,
} from "./types";

export type SearchParams = Record<string, string | number | boolean>;

export async function makeGetRequest(opts: {
  endpoint: string;
  getSearchParams: (pageParams: SearchParams) => SearchParams;
  json?: boolean;
}) {
  const { endpoint, getSearchParams, json = true } = opts;
  assert(endpoint.startsWith("/"));
  const pageParams = new window.URL(window.location.toString()).searchParams;
  const params = getSearchParams(Object.fromEntries(pageParams.entries()));
  // the params values aren't necessarily strings but they will be cast to
  // strings by the URLSearchParams constructor
  const qs = new URLSearchParams(params as Record<string, string>);
  const resourceUrl = `${__webpack_public_path__}api${endpoint}?${qs}`;
  const reqParams: RequestInit = {
    cache: "no-cache",
    credentials: process.env.NODE_ENV === "development" ? undefined : "include",
    headers: {
      Accept: "application/json",
    },
  };

  let res;
  try {
    res = await window.fetch(resourceUrl, reqParams);
  } catch (err) {
    console.log(err);
    throw err;
  }

  if (!res.ok) {
    throw new HttpException(await res.text(), res.status);
  }

  return await res[json ? "json" : "text"]();
}

export const fieldSortComparator =
  <K extends string>(key: K) =>
  <T extends { id: string }>(a: T, b: T) => {
    // if both items have the field key, used that field to sort
    if (hasKey(a, key) && a[key] && hasKey(b, key) && b[key]) {
      const valueA = a[key];
      const valueB = b[key];
      if (valueA > valueB) {
        return 1;
      }
      if (valueA < valueB) {
        return -1;
      }
      return 0;
    }

    // if only one item has the field key, it should come first
    if (hasKey(a, key) && a[key]) {
      return -1;
    }
    if (hasKey(b, key) && b[key]) {
      return 1;
    }

    // fallback to id-based sort since that will be used as the display field
    // when the sort field is missing
    if (a.id > b.id) {
      return 1;
    }
    if (a.id < b.id) {
      return -1;
    }
    return 0;
  };

type ResourcesResponseBody<Resource extends { id: string }> = {
  items: Resource[];
  metadata: ListResponseMetadata;
};

type GetAllResourcesRequestHandler<Resource extends { id: string }> = (params: {
  limit: number;
  offset: number;
}) => Promise<{ data: ResourcesResponseBody<Resource> }>;

export async function getAllResources<Resource extends { id: string }>(
  onGetAll: GetAllResourcesRequestHandler<Resource>,
  options?: { limit?: number },
): Promise<ResourcesResponseBody<Resource>> {
  const limit = options?.limit || 500;
  let offset = 0;
  let total: number;
  let results: Array<Resource> = [];
  let q: string;
  do {
    const { data } = await onGetAll({ limit, offset });
    total = data.metadata.total;
    results = results.concat(data.items);
    q = data.metadata.q;
  } while ((offset = offset + limit) < total);
  return {
    items: results,
    metadata: {
      offset: 0,
      limit: Math.ceil(total / limit) * limit,
      total,
      q,
    },
  };
}

let basePath = __webpack_public_path__.includes("://")
  ? __webpack_public_path__
  : `${window.location.origin}${__webpack_public_path__}`;
if (basePath.endsWith("/")) {
  basePath = basePath.slice(0, -1);
}
export { basePath };

// Create a new Axios instance with the desired configuration
export const axiosInstance = axios.create({
  baseURL: basePath + "/webapp",
  withCredentials: process.env.NODE_ENV !== "development",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

const allowedApi = new AllowedApi(undefined, undefined, axiosInstance);

type AxiosErrorWithResponse<T> = AxiosError<T> & {
  response: AxiosResponse<T>;
};

function isAxiosError(
  error: unknown,
): error is AxiosErrorWithResponse<unknown> {
  return (
    hasKey(error, "isAxiosError") &&
    Boolean(error.isAxiosError) &&
    hasKey(error, "response") &&
    Boolean(error.response)
  );
}

type ToolkitError = {
  code: number;
  message: string;
  status: string;
  type: string;
};

function isToolkitError(
  error: AxiosErrorWithResponse<unknown>,
): error is AxiosErrorWithResponse<{ error: ToolkitError }> {
  return !error.response.data
    ? false
    : hasKey(error.response.data, "error") &&
        hasKey(error.response.data.error, "code") &&
        hasKey(error.response.data.error, "message") &&
        hasKey(error.response.data.error, "status") &&
        hasKey(error.response.data.error, "type");
}

// TODORTK: we probably want to delete this eventually, since we have our own way of handling errors in RTK
// and delete other dead code
export async function makeToolkitRequest<T>(
  apiCall: () => Promise<AxiosResponse<T>>,
): Promise<AxiosResponse<T>> {
  try {
    const response = await apiCall();
    return response;
  } catch (err) {
    if (isAxiosError(err)) {
      const { headers } = err.response ?? {};
      const code = err.response?.status;
      const message = isToolkitError(err)
        ? err.response.data.error.message
        : err.response?.data
          ? String(err.response.data)
          : code
            ? String(code)
            : "Unknown error";
      throw new HttpException(
        message,
        code && Number(code),
        undefined,
        headers as AxiosRequestHeaders,
      );
    } else {
      throw new HttpException("Unknown error");
    }
  }
}

export async function getSingleResource<T>(
  apiCall: () => Promise<AxiosResponse<T>>,
): Promise<
  | { data: T; error?: undefined }
  | { data?: undefined; error: ResourceGetErrorStatus }
> {
  try {
    const { data } = await makeToolkitRequest(apiCall);
    return { data };
  } catch (err) {
    const { code } = err as HttpException;
    return { error: httpCodeToErrorStatus(code) };
  }
}

export async function getSingleResourceWithPermissionLookup<
  T extends { id: string; urn: string },
>(
  apiCall: () => Promise<AxiosResponse<T>>,
  action: string,
): Promise<
  | {
      data: T;
      error?: null;
      permissionLookupResult: ResourcePermissionStatus;
    }
  | {
      data?: null;
      error: ResourceGetErrorStatus;
      permissionLookupResult: ResourcePermissionStatus;
    }
> {
  const result = await getSingleResource(apiCall);
  if (result.error) {
    return {
      ...result,
      permissionLookupResult: result.error,
    };
  }
  try {
    await makeToolkitRequest(() =>
      allowedApi.v1AllowedPost({
        allowed: { resource: result.data.urn, action },
      }),
    );
    return { ...result, permissionLookupResult: "ok" };
  } catch (err) {
    const { code } = err as HttpException;
    const permissionStatus = httpCodeToPermissionStatus(code);
    if (permissionStatus) {
      return { ...result, permissionLookupResult: permissionStatus };
    }
    throw err;
  }
}

// Keeps only the text after the last \r (a progress bar rewrites one row
// over and over). Strips a single trailing \r first so a CRLF line ending
// doesn't collapse the line to nothing. Also reports whether a real \r was
// found, since only a \r-driven write may overwrite an existing row.
function collapseCarriageReturns(line: string): {
  text: string;
  isBarUpdate: boolean;
} {
  const withoutTrailingCr = line.endsWith("\r") ? line.slice(0, -1) : line;
  const lastCr = withoutTrailingCr.lastIndexOf("\r");
  return lastCr === -1
    ? { text: withoutTrailingCr, isBarUpdate: false }
    : { text: withoutTrailingCr.slice(lastCr + 1), isBarUpdate: true };
}

// Moves the cursor up so the writes that follow land back on rows already
// printed -- a terminal's way of marking "these rows get redrawn". A bar
// spanning several rows jumps back over all of them at once, so the row
// count carries the size of the block being redrawn.
// eslint-disable-next-line no-control-regex -- the control character is the pattern
const CURSOR_UP_ESCAPE = /\x1b\[(\d*)A/g;

// A terminal moves one row for both an omitted parameter and an explicit 0.
const cursorUpRows = (param: string) => Number(param) || 1;

export type LogLine = {
  text: string;
  // Rows the cursor moved up before this text was written, from an escape at
  // the very start of the raw line. That many rows were already printed, so
  // the writes landing on them are a rewrite rather than new output.
  cursorUpBefore: number;
  // Rows the cursor moved up after it, from an escape the text ran into. The
  // newline that ends the raw line cancels one of those rows back out.
  cursorUpAfter: number;
  // Whether this text came from an actual \r-driven write. Only such text
  // may overwrite a pending row; anything else must only ever append.
  isBarUpdate: boolean;
};

// One newline-delimited chunk can hold more than one logical line: the
// escape isn't always the last thing before the \n, so this can return
// several LogLines for one raw line.
export function parseLogLine(rawLine: string): LogLine[] {
  const lines: LogLine[] = [];
  let cursorUpBefore = 0;
  let textStart = 0;
  let escape: RegExpExecArray | null;
  CURSOR_UP_ESCAPE.lastIndex = 0;
  while ((escape = CURSOR_UP_ESCAPE.exec(rawLine)) !== null) {
    const rows = cursorUpRows(escape[1]);
    // An escape with no text in front of it has nothing to place: it only
    // says which row the rest of the line lands on. Several in a row are one
    // jump, which is how a writer redrawing a block of bars can express it.
    if (escape.index === textStart) {
      cursorUpBefore += rows;
      textStart = escape.index + escape[0].length;
      continue;
    }
    const { text, isBarUpdate } = collapseCarriageReturns(
      rawLine.slice(textStart, escape.index),
    );
    lines.push({ text, cursorUpBefore, cursorUpAfter: rows, isBarUpdate });
    cursorUpBefore = 0;
    textStart = escape.index + escape[0].length;
  }
  const trailing = rawLine.slice(textStart);
  if (trailing !== "" || lines.length === 0) {
    const { text, isBarUpdate } = collapseCarriageReturns(trailing);
    lines.push({ text, cursorUpBefore, cursorUpAfter: 0, isBarUpdate });
  }
  return lines;
}

// Shared by a finished job's whole body and each batch of a live stream, so
// the two can't drift in how they split or parse. Caller trims trailing \n.
export function parseLogLines(rawText: string): LogLine[] {
  return rawText ? rawText.split("\n").flatMap(parseLogLine) : [];
}

export type MergedLogLines = {
  lines: string[];
  // Row the next write lands on, which is lines.length once the cursor has
  // moved past the last row and writes simply append. Not necessarily the
  // last row: plain text appends after it without disturbing it.
  cursorRow: number;
  // Rows a cursor-up jump has left to rewrite. While any are left, even text
  // with no \r of its own overwrites its row, because the writer deliberately
  // went back over rows it had already printed.
  rewriteRows: number;
};

// Frozen so no caller can accidentally mutate this shared sentinel value.
export const EMPTY_MERGED_LOG_LINES: MergedLogLines = Object.freeze({
  lines: Object.freeze([] as string[]) as string[],
  cursorRow: 0,
  rewriteRows: 0,
}) as MergedLogLines;

// Applies a batch of parsed lines on top of what's already displayed,
// letting a redraw replace its row in place instead of appending.
export function mergeLogLines(
  state: MergedLogLines,
  newLines: LogLine[],
): MergedLogLines {
  const lines = [...state.lines];
  let { cursorRow, rewriteRows } = state;
  for (const { text, cursorUpBefore, cursorUpAfter, isBarUpdate } of newLines) {
    if (cursorUpBefore > 0) {
      const jumpedFrom = cursorRow;
      cursorRow = Math.max(0, cursorRow - cursorUpBefore);
      rewriteRows = jumpedFrom - cursorRow;
    }
    // Text that isn't rewriting a row can only ever append, even when the
    // cursor is parked on an earlier row: a log statement landing on a bar
    // has to show up after it, and the bar has to keep its row so its next
    // frame still replaces the bar rather than the statement.
    const parked =
      text !== "" &&
      !isBarUpdate &&
      rewriteRows === 0 &&
      cursorRow < lines.length;
    const row = parked ? lines.length : cursorRow;
    while (lines.length <= row) {
      lines.push("");
    }
    // Writing nothing doesn't erase a row; a terminal only clears characters
    // on an explicit erase escape.
    if (text !== "") {
      lines[row] = text;
    }
    if (!parked) {
      rewriteRows = Math.max(0, rewriteRows - 1);
      cursorRow = Math.max(0, row + 1 - cursorUpAfter);
    }
  }
  return { lines, cursorRow, rewriteRows };
}

// - buffers websocket messages until consumed
// - allows stream consumer to read payloads of complete line strings
// - possibly incomplete lines witheld from consumer until more newlines are
//   read, or until websocket source closes
// - trailing empty lines witheld from consumer until non-empty lines follow
export function webSocketTrimmedLinesStream(
  websocket: WebSocket,
): ReadableStream<LogLine[]> {
  let leftoverString = "\n";
  let cancelled = false;
  let closed = false;
  let messagesBeingDecodedCount = 0;
  let resolveNextMessage: () => void = () => null;
  let rejectNextMessage: (error: unknown) => void = () => null;

  const getNextMessagePromise = () => {
    return new Promise<void>((_resolve, _reject) => {
      resolveNextMessage = _resolve;
      rejectNextMessage = _reject;
    });
  };
  let nextMessagePromise = getNextMessagePromise();

  websocket.addEventListener("message", (e) => {
    if (cancelled) {
      return;
    }
    messagesBeingDecodedCount++;
    const reader = new FileReader();
    reader.onloadend = (e) => {
      if (!cancelled) {
        const text = (e.target?.result as string) ?? "";
        leftoverString += text;
        messagesBeingDecodedCount--;
        resolveNextMessage();
      }
    };
    reader.onerror = (err) => {
      messagesBeingDecodedCount--;
      rejectNextMessage(err);
    };
    reader.readAsText(e.data);
  });

  websocket.addEventListener("close", () => {
    closed = true;
    resolveNextMessage();
  });

  websocket.addEventListener("error", rejectNextMessage);

  return new ReadableStream<LogLine[]>({
    async pull(controller) {
      if (cancelled) {
        return;
      }
      await nextMessagePromise;
      if (cancelled) {
        return;
      }
      const finished = closed && messagesBeingDecodedCount === 0;
      // we are expecting the leftoverString to always have a leading
      // newline character. if not, there is a bug in the code.
      assert(leftoverString.startsWith("\n"));
      let trimEndIndex = leftoverString.length;
      if (!finished) {
        // wait to enqueue the last line if it doesn't end in a newline,
        // since it might be incomplete.
        while (trimEndIndex > 0 && leftoverString[trimEndIndex - 1] !== "\n") {
          trimEndIndex--;
        }
      }
      // trim trailing newlines for now and wait to enqueue it until we
      // have non-empty lines after.
      while (trimEndIndex > 0 && leftoverString[trimEndIndex - 1] === "\n") {
        trimEndIndex--;
        // account for possible carriage returns (\r\n)
        if (trimEndIndex > 0 && leftoverString[trimEndIndex - 1] === "\r") {
          trimEndIndex--;
        }
      }
      // move up one index to \n for carriage return case
      if (
        leftoverString[trimEndIndex] === "\r" &&
        leftoverString[trimEndIndex + 1] === "\n"
      ) {
        trimEndIndex++;
      }
      // trim leading newline
      const contentReady = leftoverString.slice(1, trimEndIndex);
      leftoverString = leftoverString.slice(trimEndIndex);
      controller.enqueue(parseLogLines(contentReady));
      if (finished) {
        controller.close();
      } else {
        nextMessagePromise = getNextMessagePromise();
      }
    },
    cancel(reason) {
      cancelled = true;
      resolveNextMessage();
      websocket.close(undefined, reason);
    },
  });
}
